import { StudioSystemType } from 'types/global'
import { fetchComponentsV2, V2ComponentBase } from 'util/fetchComponentsV2'
import { getSystemRoofType } from '../compatibility'
import { handleApplyMountingSetting } from '../Inputs'
import { pickSupplierDefaultByPreference } from '../supplierPreferenceDefaults'
import {
  FASTENSOL_END_CAP_CODES,
  FASTENSOL_END_CAP_VARIABLE_NAME,
  FASTENSOL_END_CLAMP_CODES,
  FASTENSOL_END_CLAMP_VARIABLE_NAME,
  FASTENSOL_MID_CLAMP_CODES,
  FASTENSOL_MID_CLAMP_VARIABLE_NAME,
  FASTENSOL_MOUNTING_ID,
  FASTENSOL_RAIL_CODES,
  FASTENSOL_RAIL_VARIABLE_NAME,
  FASTENSOL_SPLICE_CODES,
  FASTENSOL_SPLICE_VARIABLE_NAME,
  FASTENSOL_TILE_SLATE_LANDSCAPE_ROOF_HOOK_CODE,
  getFastensolRoofHookInputs,
} from './FastensolPitched'

// Alias for componentsv2 rows used by Fastensol smart defaults (rails, clamps, etc.).
export type V2ComponentRow = V2ComponentBase

export type FastensolOnApplyContext = {
  orgId: number
  supplierPreferences: string[] | null | undefined
  enabledDistributors: string[]
  supplierPreferenceEnabled: boolean | undefined
  countryIso2: string | undefined
  // Accepts optional interpolation params so the toast can plug in the list
  // of failed component types.
  translate: (key: string, params?: Record<string, unknown>) => string
}

const MISSING_STOCK_MESSAGE =
  'There are no compatible %{componentTypes} components that are stocked by your preferred supplier(s).'

// Race protection: only the most-recent invocation per system uuid may write.
// Previous in-flight controllers are aborted on re-entry so the network
// request is cancelled and the catch path treats AbortError as a no-op.
const inflightControllers = new Map<string, AbortController>()

export type FastensolSmartDefaultDropdown = {
  variableName: string
  codes: readonly string[]
  // Translation key for the dropdown's display name (already in template.json).
  labelKey: string
  /** When preference+stock yields no match, apply this SKU instead of leaving unset. */
  fallbackWithoutStock?: string
}

const FASTENSOL_STATIC_SMART_DEFAULT_DROPDOWNS: FastensolSmartDefaultDropdown[] = [
  { variableName: FASTENSOL_RAIL_VARIABLE_NAME, codes: FASTENSOL_RAIL_CODES, labelKey: 'Railing' },
  { variableName: FASTENSOL_END_CAP_VARIABLE_NAME, codes: FASTENSOL_END_CAP_CODES, labelKey: 'End Cap' },
  { variableName: FASTENSOL_END_CLAMP_VARIABLE_NAME, codes: FASTENSOL_END_CLAMP_CODES, labelKey: 'End Clamp' },
  { variableName: FASTENSOL_MID_CLAMP_VARIABLE_NAME, codes: FASTENSOL_MID_CLAMP_CODES, labelKey: 'Mid Clamp' },
  { variableName: FASTENSOL_SPLICE_VARIABLE_NAME, codes: FASTENSOL_SPLICE_CODES, labelKey: 'Splice' },
]

/**
 * Resolves all Fastensol dropdowns that receive supplier-preference smart defaults.
 * Roof hook rules are derived from `getFastensolRoofHookInputs` so candidate SKUs
 * match panel orientation (portrait / landscape / split when mixed).
 */
function getTileSlateLandscapeFallback(codes: readonly string[], roofTypeName: string | null | undefined): string | undefined {
  if (
    roofTypeName === 'Tile Slate' &&
    codes.length === 1 &&
    codes[0] === FASTENSOL_TILE_SLATE_LANDSCAPE_ROOF_HOOK_CODE
  ) {
    return FASTENSOL_TILE_SLATE_LANDSCAPE_ROOF_HOOK_CODE
  }
  return undefined
}

export function resolveFastensolSmartDefaultDropdowns(
  system: StudioSystemType,
  roofTypeName: string | null = getSystemRoofType()
): FastensolSmartDefaultDropdown[] {
  const roofHookRules: FastensolSmartDefaultDropdown[] = (
    getFastensolRoofHookInputs(system, roofTypeName).dropdownInputs ?? []
  ).map((dropdown) => {
    const codes = dropdown.options.map((option) => String(option.value))
    return {
      variableName: dropdown.variableName,
      codes,
      labelKey: dropdown.label,
      fallbackWithoutStock: getTileSlateLandscapeFallback(codes, roofTypeName),
    }
  })

  return [
    FASTENSOL_STATIC_SMART_DEFAULT_DROPDOWNS[0],
    ...roofHookRules,
    ...FASTENSOL_STATIC_SMART_DEFAULT_DROPDOWNS.slice(1),
  ]
}

export function getAllFastensolCandidateCodes(
  system: StudioSystemType,
  roofTypeName?: string | null
): string[] {
  const resolvedRoofType = roofTypeName === undefined ? getSystemRoofType() : roofTypeName
  return Array.from(
    new Set(resolveFastensolSmartDefaultDropdowns(system, resolvedRoofType).flatMap((dropdown) => dropdown.codes))
  )
}

function buildFastensolFilters(candidateCodes: string[], countryIso2: string | undefined): Record<string, string> {
  const filters: Record<string, string> = {
    codes: candidateCodes.join(','),
    limit: String(candidateCodes.length),
  }
  if (countryIso2) filters.country_iso2 = countryIso2
  return filters
}

function notifyMissingStock(
  translate: FastensolOnApplyContext['translate'],
  failedLabelKeys: string[]
): void {
  const componentTypes = failedLabelKeys.map((key) => translate(key)).join(', ')
  window.Designer?.showNotification(translate(MISSING_STOCK_MESSAGE, { componentTypes }), 'info')
}

/**
 * Fire-and-forget side-effect invoked from `useApplyMountingSystem` whenever
 * the user picks Fastensol. For each smart-defaulted dropdown (Railing, roof
 * hooks [orientation-aware], End Cap, End Clamp, Mid Clamp, Splice), picks a
 * SKU based on supplier preference + per-supplier stock and writes it into
 * `system.integration_json.fastensol[variableName]`.
 *
 * Falls back silently to the dropdowns' static defaults when the feature flag
 * is off or no preferences are configured. When prefs exist but some/all
 * dropdowns can't find an in-stock match (or the fetch fails), surfaces a
 * single combined toast naming the failed dropdown labels.
 *
 * Concurrent invocations against the same system are protected by an
 * AbortController map: re-entry aborts the prior in-flight fetch so writes
 * never race or land out of order.
 */
export async function fastensolOnApply(system: StudioSystemType, ctx: FastensolOnApplyContext): Promise<void> {
  const { supplierPreferenceEnabled, supplierPreferences, enabledDistributors } = ctx
  if (!supplierPreferenceEnabled || !supplierPreferences?.length) return

  const orderedSuppliers = supplierPreferences.filter((k) => enabledDistributors.includes(k))
  if (!orderedSuppliers.length) return

  const roofTypeName = getSystemRoofType()
  const smartDefaultDropdowns = resolveFastensolSmartDefaultDropdowns(system, roofTypeName)
  const candidateCodes = getAllFastensolCandidateCodes(system, roofTypeName)

  inflightControllers.get(system.uuid)?.abort()
  const controller = new AbortController()
  inflightControllers.set(system.uuid, controller)

  let components: V2ComponentRow[]
  try {
    components = await fetchComponentsV2<V2ComponentRow>(
      ctx.orgId,
      buildFastensolFilters(candidateCodes, ctx.countryIso2),
      { signal: controller.signal }
    )
  } catch (err) {
    // A superseded invocation aborted us — let the newer call handle the UX.
    if ((err as DOMException)?.name === 'AbortError') return
    notifyMissingStock(
      ctx.translate,
      smartDefaultDropdowns.map((dropdown) => dropdown.labelKey)
    )
    return
  } finally {
    if (inflightControllers.get(system.uuid) === controller) {
      inflightControllers.delete(system.uuid)
    }
  }

  // Bail if the user navigated away or switched mounting systems while we
  // were waiting on the fetch — writing now would clobber their new context.
  if (window.editor?.selectedSystem?.uuid !== system.uuid) return
  if (system.mounting !== FASTENSOL_MOUNTING_ID) return

  const failedLabelKeys: string[] = []
  for (const dropdown of smartDefaultDropdowns) {
    const preferred = pickSupplierDefaultByPreference(components, orderedSuppliers, dropdown.codes)
    const smart = preferred ?? dropdown.fallbackWithoutStock ?? null
    if (smart) {
      handleApplyMountingSetting(dropdown.variableName, smart, system.uuid, FASTENSOL_MOUNTING_ID)
    }
    if (!preferred) {
      failedLabelKeys.push(dropdown.labelKey)
    }
  }

  if (failedLabelKeys.length > 0) {
    notifyMissingStock(ctx.translate, failedLabelKeys)
  }
}
